import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import isEqual from 'lodash/isEqual'
import {
  defaultConfigForType,
  isCanonicalPresentationType,
  presentationTypeSlug
} from '../components/presentationManagement/defaultPresentationConfig'

const BlueprintStudioContext = createContext(null)

function emptyDraft () {
  return {
    data: { purposes: {} },
    presentation: { options: {} }
  }
}

function cloneDraft (draft) {
  return draft ? JSON.parse(JSON.stringify(draft)) : emptyDraft()
}

const CODE_REGEX = /^[a-z0-9-]+$/

// @sync-with: calc-api apps/agents/src/agents/tools/blueprint/proposeBlueprintChange.js
// Mirrors the agent-side discriminated union in propose_blueprint_change so the
// studio rejects malformed proposals (rather than silently mutating the draft).
// Returns { ok: true } on success, or { ok: false, reason } on validation failure.
function validateProposal (proposal) {
  if (!proposal || typeof proposal !== 'object') return { ok: false, reason: 'proposal is not an object' }
  const { target, op, code, value } = proposal

  if (target === 'definition') {
    if (!value || typeof value !== 'object') return { ok: false, reason: 'definition: value is required' }
    if (!value.data || typeof value.data !== 'object') return { ok: false, reason: 'definition: value.data is required' }
    if (!value.presentation || typeof value.presentation !== 'object') return { ok: false, reason: 'definition: value.presentation is required' }
    return { ok: true }
  }

  if (target === 'purpose' || target === 'presentation-option') {
    if (typeof code !== 'string' || !CODE_REGEX.test(code)) {
      return { ok: false, reason: `${target}: code must match ${CODE_REGEX}` }
    }
    if (op !== 'upsert' && op !== 'remove') {
      return { ok: false, reason: `${target}: op must be "upsert" or "remove"` }
    }
    if (op === 'upsert' && (!value || typeof value !== 'object')) {
      return { ok: false, reason: `${target}: value is required when op is "upsert"` }
    }
    return { ok: true }
  }

  return { ok: false, reason: `unknown target: ${target}` }
}

export function applyProposalToDraft (draft, proposal) {
  const validation = validateProposal(proposal)
  if (!validation.ok) {
    // eslint-disable-next-line no-console
    console.warn('applyProposalToDraft: rejected', validation.reason, proposal)
    return null
  }

  const next = cloneDraft(draft)
  const { target, op, code, value } = proposal

  if (target === 'definition') {
    next.data = JSON.parse(JSON.stringify(value.data))
    next.presentation = JSON.parse(JSON.stringify(value.presentation))
    return next
  }

  if (target === 'purpose') {
    if (!next.data) next.data = { purposes: {} }
    if (!next.data.purposes) next.data.purposes = {}
    if (op === 'remove') delete next.data.purposes[code]
    else next.data.purposes[code] = JSON.parse(JSON.stringify(value))
    return next
  }

  if (target === 'presentation-option') {
    if (!next.presentation) next.presentation = { options: {} }
    if (!next.presentation.options) next.presentation.options = {}
    if (op === 'remove') delete next.presentation.options[code]
    else next.presentation.options[code] = JSON.parse(JSON.stringify(value))
    return next
  }

  return null
}

export function BlueprintStudioProvider ({ initialDraft, blueprintId, blueprintCode, children }) {
  const [draft, setDraft] = useState(() => cloneDraft(initialDraft))
  const [appliedProposalIds, setAppliedProposalIds] = useState(() => new Set())
  const baselineRef = useRef(cloneDraft(initialDraft))

  const resetDraft = useCallback((nextBaseline) => {
    const fresh = cloneDraft(nextBaseline)
    baselineRef.current = fresh
    setDraft(cloneDraft(fresh))
    setAppliedProposalIds(new Set())
  }, [])

  const applyProposal = useCallback((proposal, proposalId) => {
    let didApply = false
    setDraft(prev => {
      const next = applyProposalToDraft(prev, proposal)
      if (next === null) return prev
      didApply = true
      return next
    })
    if (didApply && proposalId) {
      setAppliedProposalIds(prev => {
        const next = new Set(prev)
        next.add(proposalId)
        return next
      })
    }
  }, [])

  const updatePurpose = useCallback((purposeCode, nextPurposeData) => {
    setDraft(prev => {
      const next = cloneDraft(prev)
      if (!next.data) next.data = { purposes: {} }
      if (!next.data.purposes) next.data.purposes = {}
      if (nextPurposeData == null) {
        delete next.data.purposes[purposeCode]
      } else {
        next.data.purposes[purposeCode] = JSON.parse(JSON.stringify(nextPurposeData))
      }
      return next
    })
  }, [])

  const updateOptionConfig = useCallback((optionCode, nextConfig) => {
    setDraft(prev => {
      const next = cloneDraft(prev)
      if (!next.presentation) next.presentation = { options: {} }
      if (!next.presentation.options) next.presentation.options = {}
      const existing = next.presentation.options[optionCode] || {}
      next.presentation.options[optionCode] = {
        ...existing,
        config: JSON.parse(JSON.stringify(nextConfig))
      }
      return next
    })
  }, [])

  const updateOptionField = useCallback((optionCode, field, value) => {
    setDraft(prev => {
      const next = cloneDraft(prev)
      if (!next.presentation) next.presentation = { options: {} }
      if (!next.presentation.options) next.presentation.options = {}
      const existing = next.presentation.options[optionCode] || {}
      next.presentation.options[optionCode] = {
        ...existing,
        [field]: value === undefined ? null : JSON.parse(JSON.stringify(value))
      }
      return next
    })
  }, [])

  const removeOption = useCallback((optionCode) => {
    setDraft(prev => {
      const next = cloneDraft(prev)
      if (next.presentation?.options) delete next.presentation.options[optionCode]
      return next
    })
  }, [])

  // Sluggified prefix lets callers pass canonical type names like "Data Grid".
  function uniqueCode (existing, prefix) {
    const safePrefix = String(prefix || 'item')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'item'
    let n = 1
    while (existing[`${safePrefix}-${n}`]) n += 1
    return `${safePrefix}-${n}`
  }

  const addPurpose = useCallback((opts = {}) => {
    let createdCode = null
    setDraft(prev => {
      const next = cloneDraft(prev)
      if (!next.data) next.data = { purposes: {} }
      if (!next.data.purposes) next.data.purposes = {}
      const code = opts.code || uniqueCode(next.data.purposes, 'purpose')
      // Skeleton matches blueprintDataSchema so a new purpose round-trips through save.
      next.data.purposes[code] = {
        displayName: opts.displayName || 'New purpose',
        sourceQuery: opts.sourceQuery || '',
        tables: opts.tables || [],
        columns: opts.columns || [],
        masterPredicate: opts.masterPredicate || { expression: '', requiredParams: [], description: null },
        subordinatePredicates: opts.subordinatePredicates || [],
        scopePredicates: opts.scopePredicates || [],
        latch: opts.latch ?? null,
        pagination: opts.pagination || { defaultPageSize: 100, maxPageSize: 1000 }
      }
      createdCode = code
      return next
    })
    return createdCode
  }, [])

  const addOption = useCallback((opts = {}) => {
    const type = isCanonicalPresentationType(opts.type) ? opts.type : 'Data Grid'
    let createdCode = null
    setDraft(prev => {
      const next = cloneDraft(prev)
      if (!next.presentation) next.presentation = { options: {} }
      if (!next.presentation.options) next.presentation.options = {}
      const code = opts.code || uniqueCode(next.presentation.options, presentationTypeSlug(type))
      next.presentation.options[code] = {
        displayName: opts.displayName || 'New presentation option',
        type,
        config: opts.config || defaultConfigForType(type)
      }
      createdCode = code
      return next
    })
    return createdCode
  }, [])

  // Order-insensitive: spreads can reorder keys without changing logical value.
  const isDirty = useMemo(
    () => !isEqual(draft, baselineRef.current),
    [draft]
  )

  const value = useMemo(() => ({
    blueprintId,
    blueprintCode,
    draft,
    setDraft,
    resetDraft,
    applyProposal,
    appliedProposalIds,
    updatePurpose,
    updateOptionConfig,
    updateOptionField,
    removeOption,
    addPurpose,
    addOption,
    isDirty
  }), [
    blueprintId, blueprintCode, draft, resetDraft, applyProposal, appliedProposalIds,
    updatePurpose, updateOptionConfig, updateOptionField, removeOption,
    addPurpose, addOption, isDirty
  ])

  return (
    <BlueprintStudioContext.Provider value={value}>
      {children}
    </BlueprintStudioContext.Provider>
  )
}

BlueprintStudioProvider.propTypes = {
  initialDraft: PropTypes.object,
  blueprintId: PropTypes.number,
  blueprintCode: PropTypes.string,
  children: PropTypes.node
}

export function useBlueprintStudio () {
  const ctx = useContext(BlueprintStudioContext)
  if (!ctx) throw new Error('useBlueprintStudio must be used within a BlueprintStudioProvider')
  return ctx
}
